import { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import type { GeoBrowseRecurrentGene } from '../../types';
import styles from '../../GeoBrowse.module.css';

const VISIBLE_CHIP_COUNT = 3;

type GeneChipListProps = {
  selectedGenes: string[];
  recurrentGenes: GeoBrowseRecurrentGene[];
  onRemove: (symbol: string) => void;
};

export default function GeneChipList({
  selectedGenes,
  recurrentGenes,
  onRemove,
}: GeneChipListProps) {
  const [overflowOpen, setOverflowOpen] = useState(false);

  if (!selectedGenes.length) return null;

  const visible = selectedGenes.slice(0, VISIBLE_CHIP_COUNT);
  const overflow = selectedGenes.slice(VISIBLE_CHIP_COUNT);

  const renderChip = (symbol: string) => {
    const meta = recurrentGenes.find(g => g.gene_symbol === symbol);
    return (
      <span key={symbol} className={styles.geneChip}>
        {symbol}
        {meta ? ` (${meta.n_studies} studies)` : ''}
        <button
          type="button"
          className={styles.geneChipRemove}
          aria-label={`Remove ${symbol}`}
          onClick={() => onRemove(symbol)}
        >
          <XMarkIcon width={12} height={12} aria-hidden />
        </button>
      </span>
    );
  };

  return (
    <div className={styles.geneChipList}>
      {visible.map(renderChip)}

      {overflow.length > 0 && (
        <div className={styles.geneChipOverflow}>
          <button
            type="button"
            className={styles.geneChipOverflowBtn}
            aria-expanded={overflowOpen}
            onClick={() => setOverflowOpen(open => !open)}
          >
            +{overflow.length} more
          </button>
          {overflowOpen && (
            <div className={styles.geneChipOverflowMenu} role="menu">
              {overflow.map(symbol => (
                <div key={symbol} className={styles.geneChipOverflowItem} role="none">
                  {renderChip(symbol)}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
